import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, SwipeableDrawer, useMediaQuery } from "@mui/material";
import TempleDonationBox from "../../components/common/TempleDonationBox";
import useCheckoutReminder from "../../components/common/CheckoutReminder/useCheckoutReminder";
import ClosedCampaign from "../../components/common/CampaignDetailPageComponents/ClosedCampaign";
import CampaignPopup from "../../components/common/CampaignDetailPageComponents/CampaignPopup";
import ShareLink from "../../components/common/ShareLink";
import FaqSection from "../Temples/FaqSection";
import { templesData } from "../Temples/templesData";
import { buildTemplesContent } from "../Temples/cms";
import { getFeaturedTemples, getTemplesCms, getTempleDetail, getTempleSubCategories } from "../../api/services/templesService";
import { getCampaignShareDetails } from "../../api/services/campaignService";
import { getCampaignUrl } from "../../utils/slugUtils";
import TempleHero from "./TempleHero";
import HistoryEras from "./HistoryEras";
import StorySlides from "./StorySlides";
import TempleDocuments from "./TempleDocuments";
import TempleUpdates from "./TempleUpdates";
import TempleDonors from "./TempleDonors";
import TempleComments from "./TempleComments";
import { OtherTemples, TempleHowToDonate } from "./TempleSideSections";
import { buildTempleDetail } from "./templeDetailContent";
import { T, FONT } from "./shared";
import MobileHero from "./mobile/MobileHero";
import MobileHistory from "./mobile/MobileHistory";
import MobileStory from "./mobile/MobileStory";
import MobileDocuments from "./mobile/MobileDocuments";

const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Temple campaign detail page — Figma "Website Revamp" 3408:13879. Campaigns in the
// "Temples" category open here (see CampaignDetailWrapper).
const TempleDetailPage = ({ campaign: propCampaign }) => {
  const isDesktop = useMediaQuery("(min-width:1001px)");
  const isMobile = useMediaQuery("(max-width:600px)");

  const [campaignData, setCampaignData] = useState(propCampaign);
  useEffect(() => setCampaignData(propCampaign), [propCampaign]);
  const id = campaignData?.id;

  // Temple-only content from admin → Temples → Temple Detail Pages (null = not set up yet)
  const [templeDetail, setTempleDetail] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    getTempleDetail(id)
      .then((d) => !cancelled && setTempleDetail(d))
      .catch(() => !cancelled && setTempleDetail(null));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const temple = useMemo(() => buildTempleDetail(campaignData, templeDetail), [campaignData, templeDetail]);

  const [templesCms, setTemplesCms] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [otherTemples, setOtherTemples] = useState([]);
  const [templesLoading, setTemplesLoading] = useState(true);
  const [donorNames, setDonorNames] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const donationBoxRef = useRef(null);
  // Closed the Razorpay checkout without paying → stay here; Back shows the reminder popup
  const reminder = useCheckoutReminder(campaignData);

  // Temple CMS (FAQ + donation box settings), sub-categories for monthly, other temples
  useEffect(() => {
    let cancelled = false;
    getTemplesCms()
      .then((rows) => !cancelled && setTemplesCms(rows))
      .catch(() => !cancelled && setTemplesCms([]));
    getTempleSubCategories()
      .then((rows) => !cancelled && setSubCategories(rows))
      .catch(() => {});
    getFeaturedTemples()
      .then((list) => !cancelled && setOtherTemples(list.filter((t) => String(t.id) !== String(id)).slice(0, 3)))
      .catch(() => !cancelled && setOtherTemples([]))
      .finally(() => !cancelled && setTemplesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const templesContent = useMemo(() => buildTemplesContent(templesCms || [], templesData), [templesCms]);
  // The page box always offers Once and Monthly; the tab it opens on comes from the CMS
  // (Temples → Monthly Donation Box → "Tab selected when the box opens"), the same setting the
  // /temples box uses. No setting saved yet → Monthly.
  const boxConfig = useMemo(
    () => ({ ...(templesContent.donationBox || {}), donationTypes: "both", defaultMode: templesContent.donationBox?.defaultMode === "once" ? "once" : "monthly" }),
    [templesContent]
  );

  // Live raised amount (same SSE stream as the other campaign pages)
  useEffect(() => {
    if (!id) return undefined;
    const base = import.meta.env.VITE_API_BASE_URL || "";
    const es = new EventSource(`${base}/campaigns/${id}/live`);
    es.onmessage = (e) => {
      try {
        const { raised } = JSON.parse(e.data);
        if (typeof raised === "number") setCampaignData((prev) => (prev ? { ...prev, raisedAmount: raised } : prev));
      } catch (_) {
        // ignore malformed events
      }
    };
    return () => es.close();
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => setShowPopup(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const fetchShare = useCallback(async () => {
    if (!id) return;
    setShareLoading(true);
    setShareError(null);
    try {
      setShareInfo(await getCampaignShareDetails(id));
    } catch {
      setShareError("Unable to load share options right now.");
    } finally {
      setShareLoading(false);
    }
  }, [id]);

  const openShare = () => {
    if (!shareInfo && !shareLoading) fetchShare();
    setShareOpen(true);
  };

  const goDonate = () => {
    if (isDesktop) donationBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    else setDrawerOpen(true);
  };

  if (Number(campaignData?.Closedcampaign) === 1) return <ClosedCampaign campaignData={campaignData} />;

  const shareUrl =
    typeof window !== "undefined" && campaignData ? (() => {
      const url = getCampaignUrl(campaignData);
      return url ? `${window.location.origin}${url}` : "";
    })() : "";

  const donationBox = <TempleDonationBox subCategories={subCategories} config={boxConfig} campaignTitle={temple.title} campaignId={id} sourceCampaignId={id} onCheckoutAbandoned={reminder.onCheckoutAbandoned} />;

  return (
    <Box sx={{ background: "#FFFFFF", fontFamily: FONT }}>
      {/* Phones use the mobile design (Figma "Mobile revamp" 739:17212) */}
      {isMobile ? (
        <MobileHero temple={temple} donorNames={donorNames} onShare={openShare} />
      ) : (
        <TempleHero temple={temple} donorNames={donorNames} onShare={openShare} onDonate={goDonate} />
      )}

      <Box
        sx={{
          maxWidth: "1440px",
          mx: "auto",
          px: isMobile ? "16px" : isDesktop ? "30px" : "20px",
          mt: isDesktop ? "40px" : isMobile ? "32px" : "28px",
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          gap: "30px",
          alignItems: isDesktop ? "flex-start" : "stretch",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: isMobile ? "32px" : "40px", pt: isDesktop ? "30px" : 0 }}>
          {isMobile ? <MobileHistory history={temple.history} /> : <HistoryEras history={temple.history} />}
          {isMobile ? <MobileStory story={temple.story} onDonate={goDonate} /> : <StorySlides story={temple.story} onDonate={goDonate} />}
          {isMobile ? <MobileDocuments documents={campaignData?.documents || []} /> : <TempleDocuments documents={campaignData?.documents || []} />}
          <TempleUpdates updates={campaignData?.updates || []} />
          <TempleDonors campaignId={id} onTopDonors={(list) => setDonorNames(list.map((d) => d.name || "Anonymous"))} />
          <TempleComments campaignData={campaignData} />
          {/* Phones: the "How To Donate" video closes the content column (desktop has it in the sidebar) */}
          {isMobile && <TempleHowToDonate />}
        </Box>

        {isDesktop && (
          <Box sx={{ width: "534px", flexShrink: 0, position: "sticky", top: "20px", pt: "30px", display: "flex", flexDirection: "column", gap: "20px" }}>
            <Box ref={donationBoxRef} sx={{ scrollMarginTop: "40px" }}>{donationBox}</Box>
            <TempleHowToDonate />
          </Box>
        )}
      </Box>

      <OtherTemples temples={otherTemples} loading={templesLoading} />
      {templesContent.faq?.items?.length > 0 && (
        // FaqSection is shared with /temples; on phones its 56px padding doubles up with Other Temples'
        // above and the sticky bar's 80px spacer below.
        // Phones only: 32px between the Other Temples carousel dots and the FAQ panel. Desktop unchanged.
        isMobile ? (
          <Box sx={{ mt: "32px" }}>
            <FaqSection content={templesContent.faq} panel />
          </Box>
        ) : (
          <FaqSection content={templesContent.faq} panel />
        )
      )}

      <CampaignPopup isOpen={showPopup} onClose={() => setShowPopup(false)} campaignData={campaignData} />
      {reminder.modal}

      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        maxWidth={isMobile ? "xs" : "md"}
        fullWidth
        PaperProps={{ sx: { background: "transparent", boxShadow: "none", m: isMobile ? "16px" : "auto", ...(isMobile && { width: "calc(100% - 32px)", maxWidth: "100%" }) } }}
      >
        <ShareLink shareData={shareInfo} fallbackUrl={shareUrl} isLoading={shareLoading} error={shareError} onRetry={fetchShare} onClose={() => setShareOpen(false)} />
      </Dialog>

      {!isDesktop && (
        <>
          {/* Figma "Mobile revamp" 742:4741 — fixed "Donate Now" bar: full width, rounded top corners.
              Same height in every browser: the label sits in a fixed 56px band, and the iPhone
              home-indicator inset adds at most 12px (it used to add the whole inset, so some
              browsers drew a much taller red block). No CSS filter: filter on a fixed element
              renders inconsistently on iOS. */}
          <Box
            data-donation-box="true"
            component="button"
            type="button"
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              width: "100%",
              m: 0,
              border: "none",
              appearance: "none",
              WebkitAppearance: "none",
              WebkitTapHighlightColor: "transparent",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              background: T.red,
              boxShadow: "0px -6px 18px 0px rgba(64,13,13,0.14)",
              transform: "translateZ(0)",
              boxSizing: "border-box",
              height: "calc(56px + min(env(safe-area-inset-bottom, 0px), 12px))",
              pt: 0,
              pb: "min(env(safe-area-inset-bottom, 0px), 12px)",
              px: "33px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "17px",
              lineHeight: "22px",
              cursor: "pointer",
              "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "-6px" },
            }}
          >
            Donate Now
          </Box>
          <SwipeableDrawer
            anchor="bottom"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onOpen={() => setDrawerOpen(true)}
            disableSwipeToOpen
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            PaperProps={{ sx: { borderTopLeftRadius: "20px", borderTopRightRadius: "20px", maxHeight: "92vh", overflow: "auto" } }}
          >
            <Box sx={{ pt: "10px", pb: "6px", display: "flex", justifyContent: "center", position: "sticky", top: 0, background: "#FFFFFF", zIndex: 1 }}>
              <Box sx={{ width: "40px", height: "4px", background: "#E0E0E0", borderRadius: "2px" }} />
            </Box>
            <Box sx={{ p: "12px", pt: "4px", maxWidth: "560px", width: "100%", mx: "auto", boxSizing: "border-box" }}>{donationBox}</Box>
          </SwipeableDrawer>
          <Box sx={{ height: "58px" }} />
        </>
      )}
    </Box>
  );
};

export default TempleDetailPage;
